'use client'

import Image from 'next/image'
import { cn } from '@nwwp/ui-shadcn'
import { ButtonCTA } from '~/components/ButtonCTA'
import { useTrackingUrl } from '~/hooks/use-tracking-url'
import { buildScheduleRedirectUrl, IPO_STRATEGY_CALENDLY_URL, SCHEDULE_TRACKING_OPTIONS } from '~/utils/ctas'
import { getFileFromNwCDN } from '~/utils/nw-cdn'

// Route the CTA through /schedule (which renders the Calendly widget with UTM
// tracking) rather than linking to Calendly directly. /schedule allowlists the
// IPO Calendly URL, so it shows the IPO strategy event.
const IPO_SCHEDULE_HREF = buildScheduleRedirectUrl(IPO_STRATEGY_CALENDLY_URL, {})

const DEFAULT_IMAGE = getFileFromNwCDN('spacex-ipo-cta-image.webp')

const DEFAULT_HEADING = 'Want more help with your options?'

const DEFAULT_BODY =
  "Book a free IPO strategy session. We'll talk about your financial goals, review your employee equity and share some strategies to consider."

const DISCLOSURE =
  "on NerdWallet Wealth Partners' website. For informational purposes only. NerdWallet Wealth Partners does not provide tax or legal advice."

type OnsiteAdProps = {
  heading?: string
  body?: string
  ctaHref?: string
  imageSrc?: string
  segmentElement?: string
}

export const OnsiteAd = ({
  heading = DEFAULT_HEADING,
  body = DEFAULT_BODY,
  ctaHref = IPO_SCHEDULE_HREF,
  imageSrc = DEFAULT_IMAGE,
  segmentElement = 'OnsiteAd-IPO',
}: OnsiteAdProps = {}) => {
  // Append the Segment anonymous_id as salesforce_uuid (+ UTMs) at click time so
  // /schedule forwards it to Calendly and the resulting Lead is attributable in
  // the downstream funnel.
  const trackedHref = useTrackingUrl(ctaHref, SCHEDULE_TRACKING_OPTIONS)

  return (
    <aside aria-label="Advertisement" className="relative w-full rounded bg-background p-4">
      <div
        className={cn(
          'relative flex flex-col gap-4 rounded-2xl bg-card p-4 shadow-md',
          'tablet:flex-row tablet:items-center tablet:gap-10'
        )}
      >
        <div className="flex flex-1 flex-col justify-between gap-6 self-stretch">
          <div className="flex flex-col gap-2 tablet:gap-4">
            <h3 className="text-title-lg text-foreground">{heading}</h3>
            <p className="text-body-sm text-muted-foreground tablet:text-body">{body}</p>
          </div>

          <div className="relative aspect-[5/3] w-full overflow-hidden rounded-2xl tablet:hidden">
            <Image src={imageSrc} alt="" fill sizes="100vw" className="object-contain" />
          </div>

          <div className="flex flex-col gap-2">
            <ButtonCTA
              href={trackedHref}
              variant="cta-dark"
              size="cta"
              segmentElement={segmentElement}
              className="w-full"
            >
              Book your free call
            </ButtonCTA>
            <p className="text-body-xs text-muted-foreground">{DISCLOSURE}</p>
          </div>
        </div>

        <div className="relative hidden aspect-[5/3] w-full max-w-[462px] shrink-0 overflow-hidden rounded-2xl tablet:block">
          <Image src={imageSrc} alt="" fill sizes="462px" className="object-contain" />
        </div>
      </div>

      <span className="absolute top-2 right-2 inline-flex h-4 items-center justify-center rounded border border-green-lighter-2 bg-green-surface px-2 text-body-xs font-medium text-green-dark">
        AD
      </span>
    </aside>
  )
}
